import Card from "components/DesignSystem/Card/Card";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Table from "components/DesignSystem/Table/V2/Table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  CsAgent,
  CsSortOrder,
  CsTicketsSummary,
  useGetCsTicketsSectionQuery,
} from "store/apis/supportDashboard";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { CsStatCard } from "./CsStatCard";
import { StatusPill, TableSkeletonRows } from "./CsOverviewCells";
import { PERFORMANCE_SORT_OPTIONS, ticketRagStatus } from "./utils/csOverview";

const columnHelper = createColumnHelper<CsAgent>();

const columns = [
  columnHelper.accessor("name", {
    header: "Agent",
    size: 22,
    cell: (info) => (
      <span className="t-text-body-sm t-font-medium t-text-text-100">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("tickets", {
    header: "Total tickets",
    size: 12,
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("resolutionRate", {
    header: "Resolution rate",
    size: 15,
    cell: (info) => {
      const rate = info.getValue();
      const status = ticketRagStatus(rate);
      if (rate === null) return "—";

      const label = `${rate}%`;
      return <StatusPill label={label} status={status} />;
    },
  }),
  columnHelper.accessor("etdSlipped", {
    header: "ETD slipped",
    size: 13,
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("outstandingInvoices", {
    header: "Outstanding invoices",
    size: 18,
    cell: (info) => {
      return (
        <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
      );
    },
  }),
];

type TicketStatCardsProps = {
  summary: CsTicketsSummary;
};

const TicketStatCards = ({ summary }: TicketStatCardsProps) => (
  <div className="t-grid t-grid-cols-3 t-gap-3 t-mb-3">
    <CsStatCard
      label="Resolution rate"
      value={`${summary.resolutionRate}%`}
      sub="↓ Below 90% target"
    />

    <CsStatCard
      label="Overdue tickets"
      value={`${summary.overduePercent}%`}
      sub={`${summary.ticketsDue - summary.ticketsResolved} of ${
        summary.ticketsDue
      } tickets past ETD`}
    />

    <CsStatCard label="Outstanding invoices" value={summary.outstandingTotal} />
  </div>
);

type CsTicketsSectionProps = {
  summary: CsTicketsSummary;
  dateFrom: string;
  dateTo: string;
};

export const CsTicketsSection = ({
  summary,
  dateFrom,
  dateTo,
}: CsTicketsSectionProps) => {
  const query = useQuery();
  const { update: updateQuery } = useUpdateQuery();

  const page = Number(query.get("ticket_page") || "1");
  const sortOrder = (query.get("ticket_sort") as CsSortOrder) || "DSC";

  const handleSortChange = (option: { value: string } | null) => {
    if (option) updateQuery({ query: "ticket_sort", value: option.value });
  };

  const { data, isFetching } = useGetCsTicketsSectionQuery({
    start_date: dateFrom,
    end_date: dateTo,
    sort_key: "RESOLUTION_RATE",
    sort_order: sortOrder,
    page_num: page,
  });

  const agents: CsAgent[] = data?.agents ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = data?.total_pages ?? 1;

  const table = useReactTable({
    data: agents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { size: 10, minSize: 1, maxSize: 100 },
  });

  return (
    <div className="t-flex t-flex-col t-gap-3">
      <TicketStatCards summary={summary} />

      <Card.Root borderRounded="lg" className="t-overflow-hidden">
        <Card.Header>
          <Card.Title>Ticket Resolution</Card.Title>
        </Card.Header>

        <Card.Body>
          <div className="t-flex t-items-center t-justify-between">
            <div className="t-flex t-items-center t-gap-1.5">
              <span className="t-text-body-sm t-text-text-30">Sort:</span>
              <Combobox
                size="small"
                isClearable={false}
                options={PERFORMANCE_SORT_OPTIONS}
                value={PERFORMANCE_SORT_OPTIONS.find(
                  (o) => o.value === sortOrder
                )}
                onChange={(o) =>
                  handleSortChange(o as { value: string } | null)
                }
              />
            </div>
            <PaginationHandler
              page_num={page}
              per_page={5}
              total_count={totalCount}
              total_pages={totalPages}
              onPageNumChange={(p) =>
                updateQuery({ query: "ticket_page", value: String(p) })
              }
              isLoading={isFetching}
            />
          </div>
        </Card.Body>

        <Table.Container
          isLoading={isFetching}
          sticky={false}
          className="t-pt-4"
        >
          <Table.Content>
            <Table.Head>
              {table.getHeaderGroups().map((hg) => (
                <Table.HeadRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <Table.HeadCell
                      key={h.id}
                      style={{ width: `${h.getSize()}%` }}
                      isUpperCase={false}
                    >
                      {h.isPlaceholder
                        ? null
                        : flexRender(h.column.columnDef.header, h.getContext())}
                    </Table.HeadCell>
                  ))}
                </Table.HeadRow>
              ))}
            </Table.Head>
            <Table.Body>
              {isFetching ? (
                <TableSkeletonRows columns={columns} />
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}%` }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </Card.Root>
    </div>
  );
};
